import { ListKeyManager } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, booleanAttribute, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ControlDirective } from './control.directive';
import { OptionListComponent } from './option-list.component';
import { OptionRowDirective } from './option-row.directive';
import { OVERLAY_POSITIONS, SearchFieldComponent, fieldWidth } from './search-field.component';

export interface PickerOption {
  value: string;
  /** Shown instead of the raw value where the text is data — a dictionary name, not a key. */
  label?: string;
  /** Translation key shown instead of the raw value. */
  labelKey?: string;
  icon?: string;
}

/** `getLabel` is required by ListKeyManager's typeahead. */
interface PickerRow extends PickerOption {
  label: string;
  getLabel(): string;
}

const SEARCH_FROM_OPTIONS = 8;

/** Over the field, so the search box lands in the field's own footprint rather than on a row beneath it. */
const COVER_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
];

/** Row ids must be document-unique: rows are scrolled into view via getElementById. */
let nextPickerId = 0;

/**
 * Closed-list select showing the picked value, never a live search term. The list renders in a CDK
 * overlay: these pickers sit in dialogs whose own scrolling would clip an in-flow list. Where the
 * list is long enough to search, the search box opens over the field itself — the way a standard
 * select-with-search behaves — instead of taking its own row beneath it.
 */
@Component({
  selector: 'guarantees-option-picker',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, MatIcon, MatTooltip, TranslatePipe, ControlDirective, OptionListComponent, OptionRowDirective, SearchFieldComponent],
  // `relative` anchors the absolutely-positioned chevron/clear button.
  host: { class: 'relative block min-w-0' },
  templateUrl: './option-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptionPickerComponent {
  /**
   * Not `input.required`: the key manager below reads it during construction, before inputs are
   * bound, and a required input read that early throws.
   */
  options = input<PickerOption[]>([]);
  value = input<string>('');
  /**
   * The rows already taken, where several may be. Read instead of {@link value} under
   * {@link multiple}, which is a set being assembled rather than a question with one answer.
   */
  values = input<string[]>([]);
  /**
   * Picking ticks a row rather than answering with it: the list stays up for the next one, and what
   * is already taken stays on it to be unticked.
   */
  multiple = input(false, { transform: booleanAttribute });
  placeholder = input<string>('');
  emptyMessage = input<string>('');
  maxHeight = input<string>('max-h-64');
  clearable = input(true, { transform: booleanAttribute });
  /** Lets the chosen answer and the option rows run onto a second line instead of truncating. */
  wrap = input(false, { transform: booleanAttribute });
  disabled = input(false, { transform: booleanAttribute });

  /** The row acted on: the new answer, or — under {@link multiple} — the one ticked or unticked. */
  valueChange = output<string>();

  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  private readonly rowIdPrefix = `guarantees-option-${nextPickerId++}`;

  private readonly trigger = viewChild.required(CdkOverlayOrigin);

  private readonly searchBox = viewChild(SearchFieldComponent, { read: ElementRef });

  protected readonly panelWidth = signal(0);

  protected readonly open = signal(false);

  /** Narrows the list only — the answer is what was picked from it, never what was typed. */
  protected readonly search = signal('');

  private readonly rows = computed<PickerRow[]>(() =>
    this.options().map(option => {
      const label: string = option.label ?? (option.labelKey ? this.translate.instant(option.labelKey) : option.value);

      return { ...option, label, getLabel: () => label };
    })
  );

  protected readonly searchable = computed(() => this.options().length >= SEARCH_FROM_OPTIONS);

  protected readonly overlayPositions = computed(() => (this.searchable() ? COVER_POSITIONS : OVERLAY_POSITIONS));

  protected readonly selected = computed(() => this.rows().find(row => row.value === this.value()));

  protected readonly labelClass = computed(() => {
    const tone = this.selected() ? '' : 'text-neutral-400';

    return this.wrap() ? tone : `truncate ${tone}`;
  });

  protected readonly matches = computed(() => {
    const term = this.search().trim().toLowerCase();

    return term ? this.rows().filter(row => row.label.toLowerCase().includes(term)) : this.rows();
  });

  /** The keyboard behavior a native `<select>` gives for free: arrow walking, Home/End, typeahead. */
  private readonly keyManager = new ListKeyManager<PickerRow>(this.matches, inject(Injector)).withWrap().withHomeAndEnd().withTypeAhead();

  /** Mirrors the manager, which reports through a stream rather than a signal. */
  protected readonly activeIndex = signal(-1);

  constructor() {
    this.keyManager.change.pipe(takeUntilDestroyed()).subscribe(index => {
      this.activeIndex.set(index);
      this.document.getElementById(this.rowId(index))?.scrollIntoView({ block: 'nearest' });
    });

    inject(DestroyRef).onDestroy(() => this.keyManager.destroy());

    // The search box covers the field and only exists once the overlay has put it on the page, so
    // it is handed focus here rather than by whatever opened the list.
    effect(() => {
      if (this.open()) {
        this.focusSearch();
      }
    });
  }

  private focusSearch(): void {
    this.searchBox()?.nativeElement.querySelector('input')?.focus();
  }

  protected rowId(index: number): string {
    return `${this.rowIdPrefix}-${index}`;
  }

  /** Whether the row is taken: the answer given, or one of the set where several may be. */
  protected chosen(option: PickerOption): boolean {
    return this.multiple() ? this.values().includes(option.value) : option.value === this.value();
  }

  protected rowClass(option: PickerOption, index: number): string {
    if (index === this.activeIndex()) {
      return 'bg-primary-100';
    }

    return this.chosen(option) ? 'bg-primary-50' : '';
  }

  protected toggle(): void {
    if (this.open()) {
      this.close();

      return;
    }

    this.unfold();
  }

  private unfold(): void {
    this.panelWidth.set(fieldWidth(this.trigger()));
    this.search.set('');
    this.open.set(true);
    this.keyManager.setActiveItem(this.matches().findIndex(row => row.value === this.value()));
  }

  protected type(term: string): void {
    this.search.set(term);
    this.keyManager.setActiveItem(-1);
  }

  /** Escape is deliberately not handled here: the overlay takes it itself, and handling it in both places would double-close. */
  protected onFieldKeydown(event: KeyboardEvent): void {
    if (this.walked(event)) {
      return;
    }

    this.keyManager.onKeydown(event);
  }

  /** Printable keys stay in the search box — the key manager's typeahead must not swallow them. */
  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key.length === 1) {
      return;
    }

    // The overlay closes itself on Escape; focus is returned to the field it was covering.
    if (event.key === 'Escape') {
      this.trigger().elementRef.nativeElement.focus();

      return;
    }

    this.onFieldKeydown(event);
  }

  private walked(event: KeyboardEvent): boolean {
    if (!this.open()) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.unfold();
      }

      return true;
    }

    if (event.key === 'Enter') {
      const row = this.keyManager.activeItem;
      if (row) {
        // preventDefault: the button's own Enter activation would toggle the list closed again.
        event.preventDefault();
        this.pick(row.value);
      }

      return true;
    }

    return false;
  }

  /**
   * Also wired to the overlay's `detach`: Escape tears the overlay down without telling the `open`
   * binding, and left unheard the picker would believe the gone list is still open.
   */
  protected close(): void {
    this.search.set('');
    this.keyManager.setActiveItem(-1);
    this.open.set(false);
  }

  protected pick(value: string): void {
    // A set is assembled row by row, so the list stays up and focus stays where the reader is
    // working — closing on each tick would make choosing a second one a second opening.
    if (this.multiple()) {
      // Back to the box the reader is narrowing the list with: a tick taken with the mouse leaves
      // focus on the row, where neither typing nor the arrow keys reach the next one.
      this.focusSearch();
    } else {
      this.close();
      // Picking from the covering search box would otherwise drop focus on the body.
      this.trigger().elementRef.nativeElement.focus();
    }

    this.valueChange.emit(value);
  }

  protected clear(): void {
    this.search.set('');
    this.valueChange.emit('');
  }
}
